/* ══════════════════════════════════════════════════════════════════════
   MODULE SHELL — v4
   Shows truth flags prominently: proxy, incomplete, insufficient history,
   shadow truth. Not just freshness. Boris can always tell "can I trust
   this number?" from the module header.
   ══════════════════════════════════════════════════════════════════════ */

import { ErrorBoundary } from './ErrorBoundary';
import { moduleStatusChip, StatusChip, Spinner } from './StatusChip';
import { timeAgo } from '../api/format';
import type { ModuleStatus, DataAsOf, TruthFlags, StaleReason } from '../api/types';

interface ModuleShellProps {
  name: string; title: string; status: ModuleStatus; error: string | null;
  dataFreshness: DataAsOf | null; fetchedAt: number | null; isStale: boolean;
  staleReason?: StaleReason;
  truth: TruthFlags;
  onRetry: () => void; children: React.ReactNode; headerRight?: React.ReactNode;
}

export function ModuleShell({ name, title, status, error, dataFreshness, fetchedAt, isStale, staleReason, truth, onRetry, children, headerRight }: ModuleShellProps) {
  const lastRunFailed = dataFreshness?.last_run_status?.startsWith('failed');
  

  return (
    <ErrorBoundary module={name}>
      <div className="fade-up">
        {/* Module header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid var(--border-subtle)', paddingBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{title}</h2>
            {moduleStatusChip(status)}
            {/* Truth chips — visible in header, not buried */}
            {truth.isProxy && <StatusChip label="contains proxy metrics" variant="proxy" />}
            {truth.hasInsufficientHistory && <StatusChip label="insufficient history" variant="warning" />}
            {truth.hasShadowTruth && <StatusChip label="shadow data" variant="proxy" />}
            {truth.isIncomplete && <StatusChip label="incomplete" variant="stale" />}
            {truth.confidence && truth.confidence !== 'high' && (
              <StatusChip label={`${truth.confidence} confidence`} variant={truth.confidence === 'low' ? 'warning' : 'info'} />
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            {dataFreshness && (
              <span style={{ fontSize: 10, color: isStale ? 'var(--orange)' : 'var(--text-dim)' }}>
                data: {timeAgo(dataFreshness.last_success)}
              </span>
            )}
            {lastRunFailed && <span style={{ fontSize: 10, color: 'var(--red)' }}>last sync failed</span>}
            {headerRight}
          </div>
        </div>

        {/* Stale warning — different message by reason */}
        {isStale && status !== 'error' && (
          <div style={{ padding: '8px 16px', marginBottom: 12, background: staleReason === 'fetch_failing' ? 'var(--red-bg)' : 'var(--orange-bg)', border: `1px solid ${staleReason === 'fetch_failing' ? 'var(--red-border)' : 'var(--orange-border)'}`, borderRadius: 'var(--radius-md)', fontSize: 11, color: staleReason === 'fetch_failing' ? 'var(--red)' : 'var(--orange)' }}>
            {staleReason === 'fetch_failing'
              ? `⚠ Refresh failed — showing cached data from ${timeAgo(dataFreshness?.last_success)}`
              : `⚠ Data may be stale — last successful sync: ${timeAgo(dataFreshness?.last_success)}${lastRunFailed ? ` · ${dataFreshness?.last_run_status}` : ''}`
            }
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div style={{ padding: 16, marginBottom: 12, background: 'var(--red-bg)', border: '1px solid var(--red-border)', borderRadius: 'var(--radius-md)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--red)' }}>
              {error?.includes('401') || error?.includes('403')
                ? 'Authentication required — check Cloudflare Access setup'
                : error?.includes('404')
                ? `Module unavailable — ${name} endpoint returned 404`
                : `Failed to load: ${error || 'Unknown error'}`}
            </span>
            <button onClick={onRetry} style={{ padding: '4px 12px', background: 'var(--bg-active)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 11, cursor: 'pointer' }}>Retry</button>
          </div>
        )}

        {/* Loading (only if no data at all) */}
        {status === 'loading' && !fetchedAt ? (
          <div style={{ padding: 48, textAlign: 'center', background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-subtle)' }}>
            <Spinner size={20} />
            <div style={{ fontSize: 12, color: 'var(--text-ghost)', marginTop: 12 }}>Loading {title.toLowerCase()}...</div>
          </div>
        ) : children}
      </div>
    </ErrorBoundary>
  );
}
